import { Injectable } from "@angular/core";

export type ReportFrequency =
  | "daily"
  | "weekly"
  | "monthly"
  | "quarterly"
  | "halfYearly"
  | "yearly";

/** Who the automated scan report goes to: just the signed-in user, or a
 *  chosen set of users (see ScanReportSettings.userIds / allUsers). */
export type ReportSendTo = "self" | "users";

export type MapStatusKey =
  | "all"
  | "authentic"
  | "suspected"
  | "duplicate"
  | "fake"
  | "unverified";

/** Configuration > Automated Scan Reports: one scheduled email report. */
export interface ScanReportSettings {
  enabled: boolean;
  frequency: ReportFrequency;
  /** What the email includes - at least one while reports are enabled. */
  content: {
    /** Scan activity, authentication trends and protection metrics. */
    scanInsights: boolean;
    /** AI-detected protection alerts; only sent when there are alerts. */
    aiAlerts: boolean;
  };
  sendTo: ReportSendTo;
  /** sendTo "users": every user of the company, including ones added later. */
  allUsers: boolean;
  /** sendTo "users" and !allUsers: the chosen users' usrId values. */
  userIds: number[];
}

/** Null when valid, otherwise a message for the first problem found. */
export function validateScanReports(r: ScanReportSettings): string | null {
  if (!r.enabled) return null;
  if (!r.content.scanInsights && !r.content.aiAlerts) {
    return "Select at least one type of report content.";
  }
  if (r.sendTo === "users" && !r.allUsers && !r.userIds.length) {
    return "Add at least one user to send the report to.";
  }
  return null;
}

/** One scan-count band of the India map's colour scale. */
export interface MapRange {
  /** Lowest scan count in this band; the band runs up to the next one's
   *  `min` - 1 (the highest band has no upper limit). The only editable
   *  part of a band. */
  min: number;
  /** Which of the map's shades this band uses: 0 is the lightest,
   *  MAP_SHADE_COUNT - 1 the darkest (see MAP_SHADES). Fixed: band i always
   *  uses shade i. */
  shade: number;
}

/** Exactly MAP_SHADE_COUNT bands, ascending by `min`. Where the first one
 *  starts comes from the API (1 for saved ranges, 0 for the defaults'
 *  "0 scans" band) and isn't editable. Counts below it get the map's
 *  no-data colour. */
export type MapRangeList = MapRange[];

/** The API's toScan for the open-ended top band. */
export const TOP_RANGE_TO = 999999999;

export const MAP_STATUS_KEYS: MapStatusKey[] = [
  "all",
  "authentic",
  "suspected",
  "duplicate",
  "fake",
  "unverified",
];

export interface MapSettings {
  /** true: every status uses `ranges`; false: each uses statusRanges[its key].
   *  Always true now - the API stores one list for every status. */
  sameForAll: boolean;
  ranges: MapRangeList;
  statusRanges: { [K in MapStatusKey]: MapRangeList };
}

/** The ranges the map should use for one status. */
export function mapRangesFor(map: MapSettings, status: MapStatusKey | string): MapRangeList {
  if (map.sameForAll) return map.ranges;
  return (map.statusRanges as any)[status] || map.ranges;
}

/** The dashboard map's own Scan Volume colour ramps, one per status pill,
 *  lightest to darkest - exactly the shades the map has always used. A band
 *  picks a shade index, so one set of ranges works for every status. */
export const MAP_SHADES: { [K in MapStatusKey]: string[] } = {
  all: ["#dbeafe", "#bfdbfe", "#93c5fd", "#60a5fa", "#3b82f6", "#2563eb", "#1e40af"],
  authentic: ["#f0fdf4", "#dcfce7", "#bbf7d0", "#86efac", "#4ade80", "#22c55e", "#15803d"],
  suspected: ["#fff7ed", "#ffedd5", "#fed7aa", "#fdba74", "#fb923c", "#ea580c", "#c2410c"],
  duplicate: ["#fefce8", "#fef08a", "#fde047", "#facc15", "#eab308", "#ca8a04", "#a16207"],
  fake: ["#fef2f2", "#fecaca", "#fca5a5", "#f87171", "#ef4444", "#dc2626", "#b91c1c"],
  unverified: ["#f5f3ff", "#ede9fe", "#ddd6fe", "#c4b5fd", "#a78bfa", "#7c3aed", "#6d28d9"],
};
export const MAP_SHADE_COUNT = 7;

/** Colour of shade `shade` for a map status. */
export function mapShadeColor(status: MapStatusKey | string, shade: number): string {
  const ramp = (MAP_SHADES as any)[status] || MAP_SHADES.all;
  const i = Math.max(
    0,
    Math.min(MAP_SHADE_COUNT - 1, Math.round(Number(shade) || 0)),
  );
  return ramp[i];
}

/** Legend text for band `i` of an ascending range list, e.g. "501 – 2,000",
 *  "> 10,000", "0". */
export function mapRangeLabel(ranges: MapRange[], i: number): string {
  const fmt = (n: number) => Number(n).toLocaleString("en-IN");
  const min = ranges[i].min;
  const next = ranges[i + 1];
  if (!next) return i === 0 ? fmt(min) + "+" : "> " + fmt(min - 1);
  const max = next.min - 1;
  return max === min ? fmt(min) : fmt(min) + " – " + fmt(max);
}

/** Null when valid, otherwise a message for the first problem found. */
export function validateMapRanges(ranges: MapRange[]): string | null {
  if (!ranges || ranges.length !== MAP_SHADE_COUNT) {
    return `There must be ${MAP_SHADE_COUNT} ranges.`;
  }
  const first = ranges[0].min;
  if (first === null || first === undefined || !Number.isInteger(Number(first)) || first < 0) {
    return "The first range needs a whole-number start.";
  }
  // ranges[i].min is both range i+1's "From" and range i's "To" + 1.
  for (let i = 1; i < ranges.length; i++) {
    const v = ranges[i].min;
    if (v === null || v === undefined || !Number.isInteger(Number(v)) || v < 1) {
      return `Range ${i}: "To" and range ${i + 1}: "From" need a whole number.`;
    }
    if (v <= ranges[i - 1].min) {
      return `Range ${i}: "To" can't be less than its "From" (${Number(
        ranges[i - 1].min,
      ).toLocaleString("en-IN")}).`;
    }
  }
  return null;
}

export interface AppSettings {
  scanReports: ScanReportSettings;
  map: MapSettings;
}

/** Placeholder until GetConfigurationSettings answers - everything off, no
 *  recipients. Not a default: the real values only ever come from the API. */
export function blankScanReports(): ScanReportSettings {
  return {
    enabled: false,
    frequency: "daily",
    content: { scanInsights: false, aiAlerts: false },
    sendTo: "self",
    allUsers: false,
    userIds: [],
  };
}

/** One recipient as GetConfigurationSettings returns it. */
export interface EmailReportUser {
  usrId: number;
  userName: string | null;
  email: string | null;
}

/** GetConfigurationSettings' details.emailReport. */
export interface EmailReportResponse {
  /** Sent by the API but not used by the page. */
  enabledByCompany?: boolean;
  emailReportConfigId: number;
  compId: number;
  isEnabled: boolean;
  frequency: string;
  includeBrandInsights: boolean;
  includeAIAlerts: boolean;
  recipientType: string;
  modifiedDate: string;
  selectedUsers: EmailReportUser[] | null;
}

/** "Weekly", "Semi-Annual", "HalfYearly" ... -> ReportFrequency. */
function frequencyFromApi(v: string): ReportFrequency {
  const k = String(v || "").toLowerCase().replace(/[^a-z]/g, "");
  if (k === "weekly") return "weekly";
  if (k === "monthly") return "monthly";
  if (k === "quarterly") return "quarterly";
  if (
    k === "halfyearly" ||
    k === "semiannual" ||
    k === "semiannually" ||
    k === "halfyear"
  ) {
    return "halfYearly";
  }
  if (k === "yearly" || k === "annual" || k === "annually") return "yearly";
  return "daily";
}

/** GetConfigurationSettings' emailReport -> the page's ScanReportSettings.
 *  recipientType: "SelectedUsers" -> chosen users, "AllUsers" -> every
 *  user, anything else (e.g. "Self") -> the signed-in user only. */
export function scanReportsFromApi(er: EmailReportResponse): ScanReportSettings {
  const type = String(er.recipientType || "").toLowerCase().replace(/[^a-z]/g, "");
  const users = Array.isArray(er.selectedUsers) ? er.selectedUsers : [];
  return {
    enabled: !!er.isEnabled,
    frequency: frequencyFromApi(er.frequency),
    content: {
      scanInsights: !!er.includeBrandInsights,
      aiAlerts: !!er.includeAIAlerts,
    },
    sendTo: type === "selectedusers" || type === "allusers" ? "users" : "self",
    allUsers: type === "allusers",
    userIds: users.map((u) => Number(u.usrId)).filter((n) => !isNaN(n)),
  };
}


/** One band as GetConfigurationSettings / GetMapData return it
 *  (details.scanRanges, and details.defaultRanges without the ids). */
export interface ScanRangeResponse {
  scanRangeConfigId?: number;
  compId?: number;
  rangeOrder: number;
  fromScan: number;
  toScan: number;
  isActive?: boolean;
  /** Sent by GetMapData; not used. */
  isDefault?: boolean;
}

/** One band as SaveScanRangeConfig expects it. */
export interface ScanRangePayload {
  rangeOrder: number;
  fromScan: number;
  toScan: number;
}

/** A single shared list for every status, from the ranges' own order. */
export function mapSettingsFromRanges(ranges: MapRangeList): MapSettings {
  const copy = () => ranges.map((r) => ({ ...r }));
  const statusRanges: any = {};
  MAP_STATUS_KEYS.forEach((k) => (statusRanges[k] = copy()));
  return { sameForAll: true, ranges: copy(), statusRanges };
}

/** details.scanRanges -> the map's bands, or null if the list is missing or
 *  doesn't make a valid scale (wrong count, overlapping, ...). Only each
 *  band's fromScan is used - a band always runs to the next one's start - 1
 *  and the top band is open-ended. Inactive rows are skipped. */
export function mapRangesFromApi(list: ScanRangeResponse[] | null | undefined): MapRangeList | null {
  if (!Array.isArray(list)) return null;
  const ranges = list
    .filter((r) => r && r.isActive !== false)
    .slice()
    .sort((a, b) => Number(a.rangeOrder) - Number(b.rangeOrder))
    .map((r, i) => ({ min: Number(r.fromScan), shade: i }));
  return validateMapRanges(ranges) ? null : ranges;
}

/** The map's bands -> SaveScanRangeConfig's `ranges`. */
export function mapRangesToApi(ranges: MapRangeList): ScanRangePayload[] {
  return ranges.map((r, i) => {
    const next = ranges[i + 1];
    return {
      rangeOrder: i + 1,
      fromScan: Number(r.min),
      toScan: next ? Number(next.min) - 1 : TOP_RANGE_TO,
    };
  });
}

/**
 * Placeholder drafts for the Configuration page. Both Automated Scan Reports
 * and India Map Settings live only on the server (GetConfigurationSettings /
 * SaveEmailReportConfig / SaveScanRangeConfig, and the dashboard map reads
 * its bands from GetMapData) - nothing is stored in the browser. Theme lives
 * in ThemeService.
 */
@Injectable({
  providedIn: "root",
})
export class AppSettingsService {

  /** Blank drafts, shown only until the API has answered (the reports and
   *  map tabs stay on their loading state until then). */
  get(): AppSettings {
    return {
      scanReports: blankScanReports(),
      map: mapSettingsFromRanges([]),
    };
  }
}
